import { DialogComponent } from '@dialog/dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatTableDataSource, MatBottomSheet, PageEvent, MatSnackBar, MatDialog, MatPaginator, MatSort } from '@angular/material';
import { Router } from '@angular/router';
import { Subscription, Observable, of } from 'rxjs';
import { Location } from '@angular/common';

import { UserService } from '@user/user.service';
import { BottomSheetComponent } from './../../bottom-sheet/bottom-sheet.component';
import { ExceptionService } from '@exceptions-service';
import { IErrorResponse, IUser, IDialogData } from '@interfaces';
import { Role } from '@models';
import { AppService } from 'src/app/app.service';
import { ROLES, EMPTY } from '@utils';
import { CompanyService } from '@base/company/company.service';
import { ICompany } from '@base/company/company.structures';
import { concatMap } from 'rxjs/operators';
import { IFilterUsers, IResponseCollection } from '@user/structure/interfaces';

const QUERY_PARAMS = {
  search: ROLES.LEADER,
  ordering: ['-updated_at', ],
};

const CUSTOMERS_QUERY_PARAMS = {
  search: ROLES.CUSTOMER,
  ordering: ['-updated_at', ],
};

@Component({
  selector: 'app-leader-list',
  templateUrl: './leader-list.component.html',
  styleUrls: ['./leader-list.component.sass']
})
export class LeaderListComponent implements OnInit, OnDestroy {
  private subscription: Subscription;
  private observable: Observable<object>;
  private filterCustomers: IFilterUsers = null;
  private _pageIndex = 0;
  _length = 0;

  displayedColumns: string[] = [
    'email', 'first_name', 'last_name', 'is_active', 'actions', 'loginAs',
  ];
  dataSource: MatTableDataSource<IUser>;
  users: IUser[];
  updatingStatus = false;
  gettingList: Observable<boolean>;
  userRoleName = ROLES.ADMIN;

  // MatPaginator Inputs
  totalUsers: number;
  pageSize = 20;
  pageSizeOptions: number[] = [5, 10, 20];
  pageEvent: PageEvent;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private router: Router,
    private location: Location,
    public dialog: MatDialog,
    private userService: UserService,
    private mainService: AppService,
    private bottomSheet: MatBottomSheet,
    public snackBar: MatSnackBar,
    private exceptionService: ExceptionService,
    private companyService: CompanyService,
  ) {
    this.users = [];
    this.dataSource = new MatTableDataSource(this.users);
    this.gettingList = this.userService.makingRequestStatus();
    this.filterCustomers = {
      endpoint: ROLES.LEADER,
      user: null,
    };

    if (this.mainService.roles.length) {
      const userRoles = this.mainService.roles.filter((role: Role) => role.code === ROLES.LEADER);
      this.userService.selectedRole = (userRoles.length) ? userRoles[0] : null;

      this.observable = this.userService.observeUsers();
      this.subscription = this.observable.subscribe(
        (response: IResponseCollection) => {
          this.dataAdapter(response);
        },
        (err: HttpErrorResponse) => {
          const error: IErrorResponse = this.exceptionService.errorHandling(err);
          this.openSnackBar(error.message, error.title);
        }
      );

      // this.userService.getUsers(QUERY_PARAMS, ROLES.LEADER);
    } else {
      this.router.navigate(['/home/']);
    }
  }

  ngOnInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    if (typeof this.subscription !== 'undefined' && this.subscription !== null) {
      this.subscription.unsubscribe();
    }
  }

  public getServerData(event?: PageEvent) {
    this._pageIndex = event.pageIndex;
    const queryParams = {
      ordering: '-updated_at',
      page: (this._pageIndex + 1),
      page_size: event.pageSize,
      search: ROLES.LEADER
    };

    this.userService.getUsers(queryParams, ROLES.LEADER, this.filterCustomers);

    return event;
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  addUser() {
    const validRole = (this.userService.selectedRole !== null);

    if (!validRole) {
      this.openSnackBar('Intente nuevamente', 'info');
      this.location.back();
    } else {
      this.userService.setUser(EMPTY);
      this.router.navigate(['/home/users/register']);
    }
  }

  editRow(user: IUser) {
    this.userService.selectedRole = user.role;
    this.userService.setUser(user);
    this.router.navigate([`/home/${ROLES.LEADER}/${user.id}`]);
  }

  swStatus(id: number, newStatus: Boolean) {
    const title = newStatus ? 'Activar' : 'Desactivar';
    const subtitle = newStatus ? 'Autorizar' : 'Revocar';
    const sheetOptions = [
      {
        title: `${title} usuario`,
        subtitle: `${subtitle} accesos y modulos en la plataforma`,
        value: {execute: true, email: false, }
      },
      {
        title: `${title} usuario y Enviar Email`,
        subtitle: `${subtitle} accesos y modulos en la plataforma`,
        value: {execute: true, email: true, }
      },
      {
        title: 'Cancelar',
        subtitle: 'Volver al listado',
        value: {execute: false, email: false, }
      },
    ];

    // en case de desactivar usuario, no mostrar la opcion del email
    if (!newStatus) {
      const emailOption = sheetOptions.splice(1, 1);
    }

    const bsDialog = this.bottomSheet.open(
      BottomSheetComponent,
      {
        data: { options: sheetOptions },
        disableClose: true,
      }
    );

    bsDialog.afterDismissed().subscribe(changeStatus => {
      if (changeStatus['execute']) {
        this.updatingStatus = true;
        this.userService.switchStatus({id: id, is_active: newStatus, email: changeStatus['email']}).subscribe(
          response => {
            this.updatingStatus = false;
            if (response.success) {
              this.openSnackBar(newStatus ? 'Usuario Activado' : 'Usuario Desactivado', 'info');
              this.updateStatusInDataSource(id, this.users, newStatus);
            }
          },
          (err: HttpErrorResponse) => {
            this.updatingStatus = false;
            const error = this.exceptionService.errorHandling(err);
            this.openSnackBar(error.message, error.title);
          }
        );
      }
    });
  }

  openSnackBar(message: string, action: string = ''): void {
    this.snackBar.open(message, action, {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      duration: 2000,
    });
  }

  updateStatusInDataSource(nameKey, myArray, newStatus) {
    for (let i = 0; i < myArray.length; i++) {
        if (myArray[i].id === nameKey) {
          myArray[i].is_active = newStatus;
          return myArray[i];
        }
    }
  }

  deleteUser(user: IUser): void {
    const params: IDialogData = {
      strQuestion: `¿Confirma la eliminación de ${user.first_name} ${user.last_name}?`,
      strAccept: 'Aceptar',
      strCancel: 'Cancelar',
      buttonColor: 'warn',
    };
    const dialogRef = this.dialog.open(DialogComponent, {
      width: '370px',
      data: params,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((deleteUser: boolean) => {
      if (deleteUser) {
        const requests = of(
          this.userService.delete(user),
        );
        const totalRequests = requests.forEach.length;
        const listeners = requests.pipe(concatMap(obs => obs));

        listeners.subscribe(
          (response) => {
            //
          },
          (e: HttpErrorResponse) => {
            const err: IErrorResponse = this.exceptionService.errorHandling(e);
            this.openSnackBar(err.title, err.message);
          },
          () => {
            this.userService.getUsers(
              {search: ROLES.LEADER, ordering: ['-updated_at', ], },
              ROLES.LEADER
            );
            this.openSnackBar('Usuario borrado de la plataforma.', 'info');
          }
        );
      }
    });
  }

  dataAdapter(usersBody: IResponseCollection) {
    this.totalUsers = usersBody.count;
    this._length = usersBody.count;
    this.dataSource = new MatTableDataSource();
    this.dataSource.data = usersBody.results;
  }

  loginAs(user: IUser) {
    if (user.is_active) {
      this.mainService.addSession(user);

      // precargar todos los clientes...
      this.userService.getUsers(CUSTOMERS_QUERY_PARAMS, ROLES.CUSTOMER);

      // precargar todas las empresas...
      this.companyService.setCompanies([]);
      this.companyService.getCompanies(null, {ordering: ['-updated_at', ]});
      this.mainService.addSession(user);
      this.mainService.setCurrentUser(user);
      this.router.navigate(['/home/']);
    } else {
      this.openSnackBar(`${user.first_name} esta inactivo, no se puede continuar`);
    }
  }
}
